/**
 * Pending /complete retry markers.
 *
 * Lives in its own module rather than in use-gemini-direct so AuthContext can
 * call retryPendingCompletes at app start without importing the Gemini Live
 * hook — that import alone pulled the whole @google/genai SDK into the main
 * bundle, on every route. Nothing here touches the SDK: it is localStorage
 * plus a fetch.
 */
import { createLogger } from '../utils/logger';

const logger = createLogger('pending-complete');

// --- Pending /complete retry ---
// postSessionComplete's own attempt to reach POST /complete can be lost for
// good: a dropped network, a tab torn down mid-fetch (even the keepalive
// fallback has no guarantee of surviving teardown), or the tab closing before
// any of connect()'s exit paths ever ran. When that happens the session sits
// `in_progress` on the backend forever — a periodic backend sweep now
// finalizes it server-side after ~10 minutes idle (see
// app.gemini_direct.watchdog), but this is the faster, first line of
// defense: persist the request payload
// *before* sending it, so a lost attempt can be resent the next time this
// app loads, rather than waiting on the backend sweep.
const PENDING_COMPLETE_STORAGE_PREFIX = 'geminiDirectPendingComplete:';

// A marker this old is abandoned, not just delayed — drop it outright rather
// than retrying (or holding onto) it forever. Matters most on a shared/lab
// device: without this, a marker nobody ever resolves accumulates against
// the localStorage quota indefinitely.
const PENDING_COMPLETE_MAX_AGE_MS = 24 * 60 * 60 * 1000;

// 4xx statuses worth trying again later despite being non-2xx: transient or
// auth-timing issues, not a rejection of the payload itself. Every other
// 4xx — 403 "Not your session", 404, 409, 422, etc. — means resending the
// exact same request will never succeed, so the marker must be dropped
// instead of retried forever. 5xx is handled separately below: always
// retryable, since it's the server's problem, not this payload's.
const RETRYABLE_4XX_STATUSES = new Set([401, 408, 429]);

function isRetryableStatus(status: number): boolean {
  return status >= 500 || RETRYABLE_4XX_STATUSES.has(status);
}

/** One animation trigger the live model fired during the session (stored with it for QA). */
export interface FiredTriggerEntry {
  trigger_id: string;
  /** Milliseconds from the start of the session. */
  fired_at_ms: number;
  /** True when the reaction actually played; false when it was ignored or had nothing bound. */
  applied: boolean;
}

interface PendingCompletePayload {
  end_reason: string;
  transcript: Array<Record<string, unknown>>;
  active_duration_seconds: number;
  // Absent on markers saved before triggers existed, and when none fired.
  fired_triggers?: FiredTriggerEntry[];
  backend_url: string;
  // Both added so a stale marker can be told apart from a genuinely-pending
  // one without a network round trip — see retryPendingCompletes.
  saved_by_user_id?: string;
  saved_at?: number;
}

export function savePendingComplete(
  sessionId: string,
  payload: Omit<PendingCompletePayload, 'saved_at'>,
): void {
  try {
    localStorage.setItem(
      PENDING_COMPLETE_STORAGE_PREFIX + sessionId,
      JSON.stringify({ ...payload, saved_at: Date.now() }),
    );
  } catch (err) {
    // localStorage unavailable (private browsing, quota) — best-effort only;
    // the backend watchdog is still the guaranteed fallback.
    logger.debug('Could not persist pending-complete marker:', err);
  }
}

export function clearPendingComplete(sessionId: string): void {
  try {
    localStorage.removeItem(PENDING_COMPLETE_STORAGE_PREFIX + sessionId);
  } catch {
    // ignore
  }
}

// Runs once per app load (see the useEffect below): resends any /complete
// call that never got a response last time, using the current session's auth
// token and user id.
export async function retryPendingCompletes(
  backendUrlFallback: string,
  authToken: string,
  currentUserId?: string,
): Promise<void> {
  let keys: string[];
  try {
    keys = Object.keys(localStorage).filter((k) => k.startsWith(PENDING_COMPLETE_STORAGE_PREFIX));
  } catch {
    return;
  }

  for (const key of keys) {
    const sessionId = key.slice(PENDING_COMPLETE_STORAGE_PREFIX.length);
    let entry: PendingCompletePayload;
    try {
      entry = JSON.parse(localStorage.getItem(key) ?? '');
    } catch {
      localStorage.removeItem(key);
      continue;
    }

    if (entry.saved_at && Date.now() - entry.saved_at > PENDING_COMPLETE_MAX_AGE_MS) {
      logger.warn(`Discarding pending /complete for session ${sessionId} — abandoned for over a day`);
      localStorage.removeItem(key);
      continue;
    }

    // A shared/lab machine: this marker belongs to whoever was logged in
    // when it was saved, not whoever is logged in now. Sending it with the
    // *current* user's token would resend someone else's transcript under
    // the wrong identity and get a 403 that then never clears (see below) —
    // leave it for its actual owner's next login instead of touching it.
    if (entry.saved_by_user_id && currentUserId && entry.saved_by_user_id !== currentUserId) {
      continue;
    }

    try {
      const response = await fetch(
        `${entry.backend_url || backendUrlFallback}/gemini/session/${sessionId}/complete`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`,
          },
          body: JSON.stringify({
            end_reason: entry.end_reason,
            transcript: entry.transcript,
            active_duration_seconds: entry.active_duration_seconds,
            ...(entry.fired_triggers?.length ? { fired_triggers: entry.fired_triggers } : {}),
          }),
        },
      );
      if (response.ok || !isRetryableStatus(response.status)) {
        // 2xx: resent successfully. Any other non-retryable status (404
        // session gone, 403 not-your-session, 409/422 rejected payload,
        // etc.): resending the same request would only fail the same way
        // forever, so give up on it here rather than retry indefinitely.
        localStorage.removeItem(key);
        logger.info(`Resolved pending /complete for session ${sessionId} (status ${response.status})`);
      } else {
        logger.warn(`Retry of /complete for session ${sessionId} got ${response.status}, will retry next load`);
      }
    } catch (err) {
      logger.warn(`Retry of /complete for session ${sessionId} failed, will retry next load:`, err);
    }
  }
}
